// src/hooks/useLearning.js
import { useState, useEffect, useCallback } from "react";

const LEARNING_STORAGE_KEY = "samriddhi_ems_lnd_v1";

// Helper to convert any standard YouTube link into a clean embed URL
export function getYouTubeEmbedUrl(url) {
  if (!url) return "https://www.youtube.com/embed/dQw4w9WgXcQ";
  try {
    if (url.includes("embed/")) return url;
    let videoId = "";
    if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1]?.split("?")[0];
    } else if (url.includes("watch?v=")) {
      videoId = url.split("watch?v=")[1]?.split("&")[0];
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  } catch {
    return url;
  }
}

const blankState = {
  courses: [],
  progressRecords: [], // { id, courseId, employeeId, employeeName, department, videoCompleted: bool, quizAttempted: bool, quizScore: number, passed: bool, completedAt: string }
  auditLogs: [],
};

export function useLearning() {
  const [data, setData] = useState(() => {
    try {
      const saved = localStorage.getItem(LEARNING_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return blankState;
  });

  useEffect(() => {
    try {
      localStorage.setItem(LEARNING_STORAGE_KEY, JSON.stringify(data));
    } catch { /* ignore */ }
  }, [data]);

  // ── Audit Logging ────────────────────────────────────────────────────────
  const logAudit = useCallback((user, action, details) => {
    setData((prev) => ({
      ...prev,
      auditLogs: [
        {
          id: `lnd-audit-${Date.now()}`,
          user: user?.displayName || "System",
          role: user?.role || "User",
          action,
          details,
          timestamp: new Date().toLocaleString(),
        },
        ...prev.auditLogs,
      ],
    }));
  }, []);

  // ── Course Management ───────────────────────────────────────────────────
  const createCourse = useCallback((payload, user) => {
    const course = {
      id: `crs-${Date.now()}`,
      title: payload.title || "Untitled Course",
      description: payload.description || "",
      category: payload.category || "Technical",
      department: payload.department || "All Departments",
      assignedBy: user?.displayName || "HR Admin",
      duration: payload.duration || "45 Mins",
      videoUrl: payload.videoUrl || "",
      embedUrl: getYouTubeEmbedUrl(payload.videoUrl),
      status: "Active",
      assignedToIds: payload.assignedToIds || [], // employee UIDs or ['all']
      assignedToNames: payload.assignedToNames || [],
      quiz: payload.quiz || {
        passPercentage: 70,
        questions: [
          {
            id: "q1",
            question: "What is the primary objective of this course?",
            options: ["To improve efficiency", "To reduce compliance", "No objective", "To test system"],
            correctAnswer: 0,
          },
        ],
      },
      createdAt: new Date().toISOString().split("T")[0],
      createdBy: user?.displayName,
    };

    setData((prev) => ({ ...prev, courses: [course, ...prev.courses] }));
    logAudit(user, "Course Created", `"${course.title}" assigned to ${course.assignedToNames.length || 'All'} employees`);
    return course;
  }, [logAudit]);

  const updateCourse = useCallback((courseId, payload, user) => {
    setData((prev) => ({
      ...prev,
      courses: prev.courses.map((c) => {
        if (c.id !== courseId) return c;
        const videoUrl = payload.videoUrl !== undefined ? payload.videoUrl : c.videoUrl;
        return {
          ...c,
          ...payload,
          videoUrl,
          embedUrl: getYouTubeEmbedUrl(videoUrl),
          updatedAt: new Date().toISOString(),
        };
      }),
    }));
    logAudit(user, "Course Updated", `Course ${courseId} updated`);
  }, [logAudit]);

  const archiveCourse = useCallback((courseId, user) => {
    setData((prev) => ({
      ...prev,
      courses: prev.courses.map((c) =>
        c.id === courseId ? { ...c, status: c.status === "Active" ? "Archived" : "Active" } : c
      ),
    }));
    logAudit(user, "Course Status Toggled", `Course ${courseId}`);
  }, [logAudit]);

  const deleteCourse = useCallback((courseId, user) => {
    setData((prev) => ({
      ...prev,
      courses: prev.courses.filter((c) => c.id !== courseId),
      progressRecords: prev.progressRecords.filter((p) => p.courseId !== courseId),
    }));
    logAudit(user, "Course Deleted", `Course ${courseId}`);
  }, [logAudit]);

  // ── Employee Learning Progress & Quiz Workflow ────────────────────────────
  const markVideoCompleted = useCallback((courseId, employee, user) => {
    const empId = employee?.id || user?.uid || "emp-1";
    const empName = employee?.displayName || user?.displayName || "Employee";

    setData((prev) => {
      const existing = prev.progressRecords.find((p) => p.courseId === courseId && p.employeeId === empId);
      let updated;
      if (existing) {
        updated = prev.progressRecords.map((p) =>
          p.courseId === courseId && p.employeeId === empId
            ? { ...p, videoCompleted: true, updatedAt: new Date().toLocaleString() }
            : p
        );
      } else {
        updated = [
          {
            id: `prg-${Date.now()}`,
            courseId,
            employeeId: empId,
            employeeName: empName,
            department: employee?.department || "Engineering",
            videoCompleted: true,
            quizAttempted: false,
            quizScore: null,
            passed: false,
            updatedAt: new Date().toLocaleString(),
          },
          ...prev.progressRecords,
        ];
      }
      return { ...prev, progressRecords: updated };
    });
    logAudit(user, "Video Training Completed", `Course ${courseId} completed by ${empName}`);
  }, [logAudit]);

  const submitQuizAttempt = useCallback((courseId, employee, selectedAnswers, quiz, user) => {
    const empId = employee?.id || user?.uid || "emp-1";
    const empName = employee?.displayName || user?.displayName || "Employee";

    const questions = quiz?.questions || [];
    let correctCount = 0;

    questions.forEach((q, idx) => {
      if (Number(selectedAnswers[idx]) === Number(q.correctAnswer)) {
        correctCount += 1;
      }
    });

    const totalQuestions = questions.length || 1;
    const scorePct = Math.round((correctCount / totalQuestions) * 100);
    const passThreshold = quiz?.passPercentage || 70;
    const passed = scorePct >= passThreshold;
    const completedDate = new Date().toISOString().split("T")[0];

    setData((prev) => {
      const existing = prev.progressRecords.find((p) => p.courseId === courseId && p.employeeId === empId);
      let updated;
      if (existing) {
        updated = prev.progressRecords.map((p) =>
          p.courseId === courseId && p.employeeId === empId
            ? {
                ...p,
                quizAttempted: true,
                quizScore: scorePct,
                passed,
                completedAt: completedDate,
                updatedAt: new Date().toLocaleString(),
              }
            : p
        );
      } else {
        updated = [
          {
            id: `prg-${Date.now()}`,
            courseId,
            employeeId: empId,
            employeeName: empName,
            department: employee?.department || "Engineering",
            videoCompleted: true,
            quizAttempted: true,
            quizScore: scorePct,
            passed,
            completedAt: completedDate,
            updatedAt: new Date().toLocaleString(),
          },
          ...prev.progressRecords,
        ];
      }
      return { ...prev, progressRecords: updated };
    });

    logAudit(user, "Quiz Submitted", `Course ${courseId} scored ${scorePct}% (${passed ? 'PASS' : 'FAIL'}) by ${empName}`);
    return { scorePct, passed, correctCount, totalQuestions };
  }, [logAudit]);

  return {
    data,
    createCourse,
    updateCourse,
    archiveCourse,
    deleteCourse,
    markVideoCompleted,
    submitQuizAttempt,
  };
}
